//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type {
  PendingSample,
  PendingResult,
  PendingVisit,
  PendingFilters,
} from "../../services/api/domains/pending/pending.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Pending Samples & Results Composable
 *
 * Manages pending samples (not yet validated) and their associated results
 * awaiting validation and publication to LIS.
 *
 * Responsibilities:
 * - Fetch and cache pending samples with STAT/non-STAT differentiation
 * - Fetch and cache pending results for individual samples and visits
 * - Manage sample barcode updates with cache synchronization
 * - Publish individual results or entire samples to LIS
 * - Delete results and samples with proper cache invalidation
 * - Handle session storage persistence for offline resilience
 * - Track STAT samples separately for priority handling
 * - Support silent background refreshes without UI loading indicators
 *
 * @param key - Unique key for state isolation (default: "pending")
 */
export const usePending = (key = "pending") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { withAbort, abortAll } = useAbortableMultiFetch();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const error = useState<string | null>(`${key}-error`, () => null);

  const isSilentRefreshing = ref(false);
  const barcodeUpdateLoading = ref(false);
  const loading = useState<boolean>(`${key}-loading`, () => false);
  const resultsLoading = useState<boolean>(`${key}-results-loading`, () => false);

  const statSampleBarcodes = useState<Set<string>>(`${key}-stat-barcodes`, () => new Set());
  const samples = useState<PendingSample[]>(`${key}-samples`, () => {
    if (import.meta.client) {
      const cached = sessionStorage.getItem(`pending-samples-${key}`);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {
          console.error("Failed to parse cached samples:", e);
        }
      }
    }
    return [];
  });
  const visits = useState<PendingVisit[]>(`${key}-visits`, () => []);
  const sampleResults = useState<Record<string, PendingResult[]>>(`${key}-sample-results`, () => {
    if (import.meta.client) {
      const cached = sessionStorage.getItem(`pending-results-${key}`);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {
          console.error("Failed to parse cached results:", e);
        }
      }
    }
    return {};
  });
  const visitResults = useState<Record<string, PendingResult[]>>(`${key}-visit-results`, () => ({}));

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if samples are cached in memory
   *
   * Purpose:
   * Determine whether to show skeleton loading or subtle refresh indicator.
   * Used to optimize UX by avoiding unnecessary loading states.
   *
   * @returns True if any samples exist in cache
   */
  const hasCachedSamples = computed(() => samples.value.length > 0);

  /**
   * Check if results are cached for a specific sample
   *
   * Purpose:
   * Determine whether to fetch results from API or use cached data.
   * Prevents unnecessary network requests and improves performance.
   *
   * Responsibilities:
   * - Perform case-insensitive barcode lookup (converts to uppercase)
   * - Return true only if results array exists (not empty check)
   *
   * @param barcode - Sample barcode to check (case-insensitive)
   * @returns True if results are cached for this sample
   */
  const hasCachedResultsFor = (barcode: string) => {
    return !!sampleResults.value[barcode.toUpperCase()];
  };

  //
  // ============================================================================
  // METHODS - SAMPLES
  // ============================================================================
  //

  /**
   * Fetch STAT (urgent priority) samples
   *
   * Purpose:
   * Retrieve only samples marked as STAT (urgent) that require immediate attention.
   * STAT samples must have valid visit information to be considered urgent.
   *
   * Responsibilities:
   * - Apply STAT filter to API request
   * - Filter out samples without visitId (invalid STAT samples)
   * - Return only validated STAT samples with complete visit data
   * - Handle abort signals for cancellable requests
   *
   * @param filters - Optional filter criteria (merged with stat: true)
   * @returns Array of validated STAT samples, empty array on abort/error
   */
  const fetchStatSamples = async (filters?: PendingFilters): Promise<PendingSample[]> => {
    try {
      const statFilters = { ...filters, stat: true };
      const response = await withAbort<PendingSample[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchPendingSamples(statFilters, signal);
      });

      // Filter out samples without visit information
      // Samples without visitId cannot be STAT (requires valid visit data)
      const validStatSamples = (response || []).filter(
        (sample) => sample.visitId != null && sample.visitId.trim() !== "",
      );

      return validStatSamples;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchStatSamples",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return [];
      throw err;
    }
  };

  /**
   * Fetch non-STAT (routine) samples
   *
   * Purpose:
   * Retrieve samples with normal priority that don't require immediate attention.
   * These samples follow standard validation workflow without urgency constraints.
   *
   * Responsibilities:
   * - Apply non-STAT filter to API request
   * - Return all routine samples regardless of visit status
   * - Handle abort signals for cancellable requests
   * - Return empty array on abort/error to prevent UI issues
   *
   * @param filters - Optional filter criteria (merged with stat: false)
   * @returns Array of non-STAT samples, empty array on abort/error
   */
  const fetchNonStatSamples = async (filters?: PendingFilters): Promise<PendingSample[]> => {
    try {
      const nonStatFilters = { ...filters, stat: false };
      const response = await withAbort<PendingSample[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchPendingSamples(nonStatFilters, signal);
      });
      return response || [];
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchNonStatSamples",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return [];
      throw err;
    }
  };

  /**
   * Fetch pending samples with intelligent caching and STAT prioritization
   *
   * Purpose:
   * Main entry point for retrieving pending samples with smart loading states,
   * session storage persistence, and STAT/non-STAT separation.
   *
   * Responsibilities:
   * - Determine fetch strategy based on filter.stat value:
   *   * stat: true → ONLY fetch STAT samples
   *   * stat: false → ONLY fetch non-STAT samples
   *   * stat: undefined → fetch BOTH in parallel (backward compatibility)
   * - Manage loading indicators (normal vs silent refresh)
   * - Merge STAT and non-STAT results without duplicates
   * - Build and maintain STAT barcode set for priority sorting
   * - Persist samples to session storage for offline resilience
   * - Preserve original fetch order on first load
   * - Handle request abortion gracefully
   *
   * @param filters - Optional filter criteria (location, instrument, dateRange, etc.)
   * @param forceRefresh - If true, ignore cache and fetch fresh data
   * @param silent - If true, use background loading state instead of skeleton
   * @returns Array of pending samples or null if request was aborted
   */
  const fetchPendingSamples = async (
    filters?: PendingFilters,
    forceRefresh = false,
    silent = false,
  ): Promise<PendingSample[] | null> => {
    // Track silent vs normal loading
    if (silent) {
      isSilentRefreshing.value = true;
    } else {
      if (!hasCachedSamples.value || forceRefresh) {
        loading.value = true;
      }
    }

    error.value = null;

    try {
      let statSamples: PendingSample[] = [];
      let nonStatSamples: PendingSample[] = [];

      // Handle STAT filtering - supports 3 modes:
      // stat=true: Only STAT samples, stat=false: Only non-STAT, undefined: Both
      if (filters?.stat === true) {
        statSamples = await fetchStatSamples(filters);
      } else if (filters?.stat === false) {
        nonStatSamples = await fetchNonStatSamples(filters);
      } else {
        // Fetch both types in parallel for efficiency
        [statSamples, nonStatSamples] = await Promise.all([fetchStatSamples(filters), fetchNonStatSamples(filters)]);
      }

      // Check if request was aborted
      if (statSamples === null && nonStatSamples === null) {
        return null;
      }

      // Build STAT set for O(1) lookups
      const statBarcodes = new Set<string>(statSamples.map((s) => s.barcode));

      // Merge arrays, filtering duplicates (prefer STAT version if duplicate)
      const allSamples = [...statSamples, ...nonStatSamples.filter((s) => !statBarcodes.has(s.barcode))];

      statSampleBarcodes.value = statBarcodes;
      samples.value = allSamples;

      // Persist to session storage for offline access and cross-tab sync
      if (import.meta.client) {
        // Preserve original fetch order for consistent sorting
        const orderKey = `pending-original-order-${key}`;
        const existingOrder = sessionStorage.getItem(orderKey);

        if (!existingOrder) {
          // First fetch - create order map with barcode->position
          const orderMap = new Map<string, number>();
          allSamples.forEach((sample, index) => {
            orderMap.set(sample.barcode, index);
          });
          sessionStorage.setItem(orderKey, JSON.stringify(Array.from(orderMap)));
        }

        sessionStorage.setItem(`pending-samples-${key}`, JSON.stringify(allSamples));
        sessionStorage.setItem(`pending-stat-barcodes-${key}`, JSON.stringify(Array.from(statSampleBarcodes.value)));
      }

      return allSamples;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchPendingSamples",
        fallbackKey: "components.common.errors.fetchFailed",
      });

      // Handle abort silently
      if (result.type === "abort") {
        return null;
      }

      // Set specific error message based on type
      if (result.statusCode === 404) {
        error.value = "API endpoint not found (404). Please check your configuration.";
      } else if (result.type === "server") {
        error.value = "Server error (500). Please try again later.";
      } else if (result.type === "auth") {
        error.value = "Authentication error. Please log in again.";
      } else if (result.type === "network") {
        error.value = "Network error. Please check your internet connection.";
      } else {
        error.value = result.message || "Failed to fetch pending samples";
      }

      return null;
    } finally {
      // Clear loading flags
      if (silent) {
        isSilentRefreshing.value = false;
      } else {
        loading.value = false;
      }
    }
  };

  /**
   * Check if sample is marked as STAT (urgent priority)
   *
   * Purpose:
   * Quick lookup to determine if a sample requires urgent handling.
   * Used for UI badges, sorting, and priority-based filtering.
   *
   * Responsibilities:
   * - Perform exact match against STAT barcode set
   * - Return boolean for immediate conditional logic
   *
   * @param barcode - Sample barcode to check (case-sensitive)
   * @returns True if sample is STAT, false otherwise
   */
  const isStat = (barcode: string): boolean => {
    return statSampleBarcodes.value.has(barcode);
  };

  /**
   * Fetch pending results for a specific sample
   *
   * Purpose:
   * Retrieve all unvalidated test results awaiting review for a sample.
   * Results are cached per sample to minimize API calls during validation workflow.
   *
   * Responsibilities:
   * - Check cache before fetching (unless force refresh)
   * - Show loading only when fetching without cache
   * - Store results with uppercase barcode key for consistency
   * - Persist results to session storage for offline access
   * - Handle request abortion (returns null)
   * - Clear loading state after completion
   *
   * @param barcode - Sample barcode (case-insensitive)
   * @param forceRefresh - If true, bypass cache and fetch fresh results
   * @returns Array of pending results or null if request was aborted
   */
  const fetchPendingSampleResults = async (barcode: string, forceRefresh = false): Promise<PendingResult[] | null> => {
    // Only show loading if no cached data OR force refresh
    if (!hasCachedResultsFor(barcode) || forceRefresh) {
      resultsLoading.value = true;
    }

    // Do NOT touch error.value here — that field belongs to the sample-list
    // fetch (fetchPendingSamples). Writing it here causes the global network
    // error banner to flash whenever a result-fetch is aborted mid-flight
    // (e.g. user switches samples rapidly).

    try {
      const response = await withAbort<PendingResult[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchPendingSampleResults(barcode, signal);
      });

      if (response) {
        sampleResults.value[barcode.toUpperCase()] = response;

        // Save to session storage
        if (import.meta.client) {
          sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
        }
      }
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchPendingSampleResults",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      // Abort is not an error — caller already handles null return
      if (result.type === "abort") return null;
      // Log non-abort errors but do not surface them as a global banner
      console.error("[fetchPendingSampleResults]", result.message);
      return null;
    } finally {
      resultsLoading.value = false;
    }
  };

  /**
   * Clear all cached pending data
   *
   * Purpose:
   * Force a complete cache reset to fetch fresh data from API.
   * Used when user explicitly requests refresh or after significant data changes.
   *
   * Responsibilities:
   * - Remove all session storage keys for pending data
   * - Clear in-memory samples array
   * - Clear in-memory results cache
   * - Reset STAT barcode set
   *
   * Note: Does NOT abort ongoing requests - call abortAll() separately if needed
   */
  const clearCache = (): void => {
    if (import.meta.client) {
      sessionStorage.removeItem(`pending-samples-${key}`);
      sessionStorage.removeItem(`pending-results-${key}`);
      sessionStorage.removeItem(`pending-stat-barcodes-${key}`);
    }

    samples.value = [];
    sampleResults.value = {};
  };

  /**
   * Update sample barcode with intelligent merge/rename handling
   *
   * Purpose:
   * Change a sample's barcode while properly handling cache synchronization
   * and potential merge scenarios when target barcode already exists.
   *
   * Responsibilities:
   * - Send barcode update request to API
   * - Detect merge vs rename scenarios:
   *   * MERGE: Both old and new barcodes exist → remove old sample, delete both result caches
   *   * RENAME: Only old barcode exists → update barcode in place, move result cache
   * - Update sample barcode in samples array
   * - Move/delete result caches appropriately
   * - Update STAT barcode set if applicable
   * - Persist all changes to session storage
   * - Handle request abortion gracefully
   *
   * Business Logic - Merge Scenario:
   * When merging (new barcode exists):
   * 1. Remove old sample from list (duplicate)
   * 2. Delete old results cache (orphaned)
   * 3. Delete new results cache (force refetch to get merged data)
   * 4. This prevents stale data and ensures API is source of truth
   *
   * Business Logic - Rename Scenario:
   * When renaming (new barcode doesn't exist):
   * 1. Update barcode in samples array
   * 2. Move results cache from old key to new key
   * 3. Update STAT set if sample was STAT
   *
   * @param oldBarcode - Current sample barcode
   * @param newBarcode - New barcode to assign
   * @returns True if successful, false if aborted or error
   */
  const updateSampleBarcode = async (oldBarcode: string, newBarcode: string): Promise<boolean> => {
    barcodeUpdateLoading.value = true;
    error.value = null;

    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.updateSampleBarcode(oldBarcode, newBarcode, signal);
      });

      // Remove duplicates after update
      // If newBarcode already exists, this is a merge - remove the old sample
      const oldKey = oldBarcode.toUpperCase();
      const newKey = newBarcode.toUpperCase();

      // Check if merge scenario (both samples exist)
      const oldSampleExists = samples.value.some((s) => s.barcode.toUpperCase() === oldKey);
      const newSampleExists = samples.value.some((s) => s.barcode.toUpperCase() === newKey);

      if (oldSampleExists && newSampleExists) {
        // MERGE: Remove old sample
        samples.value = samples.value.filter((s) => s.barcode.toUpperCase() !== oldKey);

        // CRITICAL: Delete old results cache (don't merge!)
        delete sampleResults.value[oldKey];

        // CRITICAL: Delete new results cache (force refetch)
        delete sampleResults.value[newKey];
      } else {
        // RENAME: Update barcode in place
        const sampleIndex = samples.value.findIndex((s) => s.barcode.toUpperCase() === oldKey);
        if (sampleIndex !== -1) {
          samples.value[sampleIndex]!.barcode = newBarcode;
        }

        // Move results cache (simple rename)
        if (sampleResults.value[oldKey]) {
          sampleResults.value[newKey] = sampleResults.value[oldKey];
          delete sampleResults.value[oldKey];
        }
      }

      // Move results cache to new barcode
      if (sampleResults.value[oldKey]) {
        // If new barcode already has results, merge them
        if (sampleResults.value[newKey]) {
          sampleResults.value[newKey] = [...sampleResults.value[newKey], ...sampleResults.value[oldKey]];
        } else {
          sampleResults.value[newKey] = sampleResults.value[oldKey];
        }
        delete sampleResults.value[oldKey];
      }

      // Update STAT cache
      if (statSampleBarcodes.value.has(oldBarcode)) {
        statSampleBarcodes.value.delete(oldBarcode);
        statSampleBarcodes.value.add(newBarcode);
      }

      // Update sessionStorage
      if (import.meta.client) {
        sessionStorage.setItem(`pending-samples-${key}`, JSON.stringify(samples.value));
        sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
        sessionStorage.setItem(`pending-stat-barcodes-${key}`, JSON.stringify(Array.from(statSampleBarcodes.value)));
      }

      return true;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "updateSampleBarcode",
        fallbackKey: "components.common.errors.updateFailed",
      });
      if (result.type === "abort") return false;
      error.value = result.message || "Failed to update sample barcode";
      return false;
    } finally {
      barcodeUpdateLoading.value = false;
    }
  };

  /**
   * Delete all results for a sample
   *
   * Purpose:
   * Remove all pending test results for a sample without deleting the sample itself.
   * Used when results need to be completely redone or were entered incorrectly.
   *
   * Responsibilities:
   * - Send delete request to API
   * - Remove results from in-memory cache
   * - Update session storage to reflect deletion
   * - Remove STAT status if sample was STAT
   * - Update STAT cache in session storage
   * - Handle request abortion gracefully
   *
   * @param sampleId - Sample barcode (case-insensitive, stored as uppercase)
   * @returns True if successful, false if aborted or error
   */
  const deleteAllResults = async (sampleId: string): Promise<boolean> => {
    error.value = null;

    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.deleteSample(sampleId, signal);
      });

      // Delete results cache
      delete sampleResults.value[sampleId.toUpperCase()];

      // Update sessionStorage to match in-memory state
      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));

        // Also update STAT cache if this was a STAT sample
        if (statSampleBarcodes.value.has(sampleId)) {
          statSampleBarcodes.value.delete(sampleId);
          sessionStorage.setItem(`pending-stat-barcodes-${key}`, JSON.stringify(Array.from(statSampleBarcodes.value)));
        }
      }

      return true;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "deleteSample",
        fallbackKey: "components.common.errors.deleteFailed",
      });
      if (result.type === "abort") return false;
      error.value = result.message || `Failed to delete sample ${sampleId}`;
      return false;
    }
  };

  /**
   * Publish all results for a sample to LIS
   *
   * Purpose:
   * Validate and send all pending results for a sample to the LIS system.
   * This is the final step in the validation workflow.
   *
   * Responsibilities:
   * - Send publish request to API for all sample results
   * - Analyze response for partial success/failure scenarios
   * - Only remove sample from cache if ALL results succeeded
   * - Update session storage after successful publication
   * - Remove STAT status after successful publication
   * - Handle request abortion gracefully
   * - Return response with status details for each result
   *
   * @param sampleId - Sample barcode (case-insensitive)
   * @returns API response with status for each result, or null if aborted
   */
  const publishAllResults = async (sampleId: string): Promise<any> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.publishSample(sampleId, signal);
      });

      // Check response before removing sample
      if (response && Array.isArray(response)) {
        const errors = (response as any[]).filter((r) => r.status === 2);
        const successes = (response as any[]).filter((r) => r.status === 1);

        // If all failed, don't remove sample
        if (errors.length > 0 && successes.length === 0) {
          return response;
        }
      }

      // Only remove sample if all succeeded
      delete sampleResults.value[sampleId.toUpperCase()];

      // Update sessionStorage
      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));

        // Also update STAT cache
        if (statSampleBarcodes.value.has(sampleId)) {
          statSampleBarcodes.value.delete(sampleId);
          sessionStorage.setItem(`pending-stat-barcodes-${key}`, JSON.stringify(Array.from(statSampleBarcodes.value)));
        }
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "publishAllResults",
        fallbackKey: "components.common.errors.publishFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || `Failed to publish sample ${sampleId}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  //
  // ============================================================================
  // METHODS - RESULTS
  // ============================================================================
  //

  /**
   * Fetch a specific result by ID
   *
   * Purpose:
   * Retrieve detailed information for a single pending result.
   * Used for result editing, detailed view, or validation dialogs.
   *
   * Responsibilities:
   * - Fetch result from API by unique ID
   * - Handle request abortion
   * - Return result data or null
   *
   * Note: Does NOT cache result data (single-use fetch)
   *
   * @param resultId - Unique result identifier
   * @returns Result details or null if aborted/error
   */
  const fetchResultById = async (resultId: number): Promise<PendingResult | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort<PendingResult>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchResultById(resultId, signal);
      });

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchResultById",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || `Failed to fetch result ${resultId}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Delete a specific result
   *
   * Purpose:
   * Remove a single test result from pending validation queue.
   * Used when a specific test result is incorrect or needs to be rerun.
   *
   * Responsibilities:
   * - Send delete request to API
   * - Remove result from ALL cached sample results
   * - Update session storage after cache modification
   * - Handle request abortion gracefully
   *
   * @param resultId - Unique result identifier
   * @returns True if successful, false if aborted or error
   */
  const deleteResult = async (resultId: number): Promise<boolean> => {
    error.value = null;

    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.deleteResult(resultId, signal);
      });

      // UPDATE CACHE: Remove result from cached data
      Object.keys(sampleResults.value).forEach((barcode) => {
        const results = sampleResults.value[barcode];
        if (results) {
          sampleResults.value[barcode] = results.filter((r) => r.id !== resultId);

          // Update session storage
          if (import.meta.client) {
            sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
          }
        }
      });

      return true;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "deleteResult",
        fallbackKey: "components.common.errors.deleteFailed",
      });
      if (result.type === "abort") return false;
      error.value = result.message || "Failed to delete result";
      return false;
    }
  };

  /**
   * Publish a specific result to LIS
   *
   * Purpose:
   * Validate and send a single test result to the LIS system.
   * Allows granular control over which results to publish vs publishing entire sample.
   *
   * Responsibilities:
   * - Send publish request for single result
   * - Analyze response for success/failure status
   * - Only remove from cache if status indicates success (status !== 2)
   * - Update all relevant sample caches (result might be in multiple)
   * - Persist cache changes to session storage
   * - Return response with validation status
   *
   * @param resultId - Unique result identifier
   * @returns API response with validation status, or null if aborted
   */
  const publishResult = async (resultId: number): Promise<any> => {
    resultsLoading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.publishResult(resultId, signal);
      });

      // Check response before updating cache
      if (response && Array.isArray(response)) {
        const result = response[0];

        // If error status, don't update cache
        if (result?.status === 2) {
          return response; // Return error response without updating cache
        }
      }

      // Only update cache if success
      Object.keys(sampleResults.value).forEach((barcode) => {
        const results = sampleResults.value[barcode];
        if (results) {
          sampleResults.value[barcode] = results.filter((r) => r.id !== resultId);

          // Update session storage
          if (import.meta.client) {
            sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
          }
        }
      });

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "publishResult",
        fallbackKey: "components.common.errors.publishFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to publish result";
      return null;
    } finally {
      resultsLoading.value = false;
    }
  };

  /**
   * Update a result value
   *
   * Purpose:
   * Edit a pending result value and track the edit history.
   *
   * Behavior:
   * - API call to update result value with edit metadata
   * - Update cache for all samples containing this result
   * - Mark result as edited with history entry
   * - Persist cache changes to session storage
   * - Return success status
   *
   * @param resultId - Unique result identifier
   * @param newValue - New result value
   * @param editedBy - User who made the edit
   * @param reason - Optional reason for the edit
   * @param originalValue - Original value before first edit (for proper history tracking)
   * @returns True if successful, false otherwise
   */
  const updateResult = async (
    resultId: number,
    newValue: string,
    editedBy: string,
    reason?: string,
    originalValue?: string,
  ): Promise<boolean> => {
    resultsLoading.value = true;
    error.value = null;

    try {
      // Call Nuxt server route directly (demo endpoint)
      const response = await $fetch<{ success: boolean; result: any }>(`/api/pending/results/${resultId}`, {
        method: "PATCH",
        body: {
          newValue,
          editedBy,
          reason,
          originalValue,
        },
      });

      if (!response || !response.success) {
        throw new Error("Failed to update result");
      }

      // Update cache for all samples containing this result
      let foundAndUpdated = false;
      const updatedCache = { ...sampleResults.value };

      Object.keys(updatedCache).forEach((barcode) => {
        const barcodeResults = updatedCache[barcode];
        if (!barcodeResults) return;

        const resultIndex = barcodeResults.findIndex((r) => r.id === resultId);

        if (resultIndex !== -1) {
          foundAndUpdated = true;

          const existingResult = barcodeResults[resultIndex];
          if (!existingResult) return;

          // Update the result with new value and edit metadata from API response
          const updatedResult: PendingResult = {
            ...existingResult,
            displayResult: newValue,
            textResult: newValue,
            numericResult: parseFloat(newValue) || existingResult.numericResult,
            isEdited: true,
            editHistory: response.result.editHistory || existingResult.editHistory || [],
          };

          // Create a new array to trigger reactivity
          updatedCache[barcode] = [
            ...barcodeResults.slice(0, resultIndex),
            updatedResult,
            ...barcodeResults.slice(resultIndex + 1),
          ];
        }
      });

      // Replace the entire cache object to trigger reactivity
      sampleResults.value = updatedCache;

      // Also update selectedSampleResults if it exists (for immediate UI update)
      try {
        // Use useState directly to access the shared state
        const selectedSampleResults = useState<any[]>("validation.selectedSampleResults", () => []);

        const selectedResultIndex = selectedSampleResults.value.findIndex((r: any) => r.id === resultId);

        if (selectedResultIndex !== -1) {
          const updatedSelectedResult = {
            ...selectedSampleResults.value[selectedResultIndex],
            displayResult: newValue,
            textResult: newValue,
            numericResult: parseFloat(newValue) || selectedSampleResults.value[selectedResultIndex].numericResult,
            isEdited: true,
            editHistory:
              response.result.editHistory || selectedSampleResults.value[selectedResultIndex].editHistory || [],
          };

          // Create new array to trigger reactivity
          selectedSampleResults.value = [
            ...selectedSampleResults.value.slice(0, selectedResultIndex),
            updatedSelectedResult,
            ...selectedSampleResults.value.slice(selectedResultIndex + 1),
          ];
        }
      } catch (e) {
        // Selection state might not exist in some contexts, that's ok
      }

      // Persist to session storage
      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
      }

      return true;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "updateResult",
        fallbackKey: "components.common.errors.updateFailed",
      });
      if (result.type === "abort") return false;
      error.value = result.message || `Failed to update result ${resultId}`;
      return false;
    } finally {
      resultsLoading.value = false;
    }
  };

  //
  // ============================================================================
  // METHODS - VISITS
  // ============================================================================
  //

  /**
   * Fetch pending visits with optional filtering
   *
   * Purpose:
   * Retrieve list of patient visits that have pending results awaiting validation.
   * Used for visit-based validation workflow (alternative to sample-based).
   *
   * Responsibilities:
   * - Fetch visits from API with optional filters
   * - Store visits in state for access
   * - Handle request abortion
   * - Return visit list or null
   *
   * Note: filterUnknown is excluded from filters (not applicable to visits)
   *
   * @param filters - Optional filter criteria (location, dateRange, etc.)
   * @returns Array of pending visits or null if aborted/error
   */
  const fetchPendingVisits = async (
    filters?: Omit<PendingFilters, "filterUnknown">,
  ): Promise<PendingVisit[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort<PendingVisit[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchPendingVisits(filters, signal);
      });

      if (response) visits.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchPendingVisits",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch pending visits";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch pending results for a specific visit
   *
   * Purpose:
   * Retrieve all unvalidated test results associated with a patient visit.
   * Supports visit-based validation workflow where results are grouped by visit.
   *
   * Responsibilities:
   * - Fetch results from API for specified visit
   * - Store results in visit-specific cache
   * - Handle request abortion
   * - Return results array or null
   *
   * @param visitId - Unique visit identifier from LIS
   * @returns Array of pending results for visit or null if aborted/error
   */
  const fetchPendingVisitResults = async (visitId: string): Promise<PendingResult[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort<PendingResult[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.pending.fetchPendingVisitResults(visitId, signal);
      });

      if (response) visitResults.value[visitId] = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchPendingVisitResults",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || `Failed to fetch results for visit ${visitId}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Move cached results when sample barcode changes
   *
   * Purpose:
   * Synchronize result cache keys after barcode update operation.
   * Must be called after updateSampleBarcode to maintain cache consistency.
   *
   * Responsibilities:
   * - Move results from old barcode key to new barcode key
   * - Convert both keys to uppercase for consistency
   * - Delete old cache entry
   * - Persist updated cache to session storage
   *
   * Note: This is a cache-only operation, no API call
   *
   * @param oldBarcode - Original sample barcode
   * @param newBarcode - New sample barcode
   */
  const moveCachedResults = (oldBarcode: string, newBarcode: string): void => {
    const oldKey = oldBarcode.toUpperCase();
    const newKey = newBarcode.toUpperCase();

    // Move results cache
    if (sampleResults.value[oldKey]) {
      sampleResults.value[newKey] = sampleResults.value[oldKey];
      delete sampleResults.value[oldKey];

      // Update session storage
      if (import.meta.client) {
        sessionStorage.setItem(`pending-results-${key}`, JSON.stringify(sampleResults.value));
      }
    }
  };

  //
  // ============================================================================
  // HELPER METHODS
  // ============================================================================
  //

  /**
   * Clear all cached data (both samples and visits)
   *
   * Purpose:
   * Complete state reset for fresh data fetch or context switch.
   * More aggressive than clearCache() - clears ALL pending data.
   *
   * Responsibilities:
   * - Clear samples array
   * - Clear visits array
   * - Clear sample results cache
   * - Clear visit results cache
   *
   * Note: Does NOT clear session storage - use clearCache() for that
   * Note: Does NOT abort requests - call abortAll() separately
   */
  const clearAll = (): void => {
    samples.value = [];
    visits.value = [];
    sampleResults.value = {};
    visitResults.value = {};
  };

  return {
    // State
    error,

    loading: readonly(loading),
    isSilentRefreshing: readonly(isSilentRefreshing),
    barcodeUpdateLoading: readonly(barcodeUpdateLoading),
    resultsLoading: readonly(resultsLoading),

    samples,
    visits,
    sampleResults,
    visitResults,

    // Computed
    hasCachedSamples: readonly(hasCachedSamples),
    hasCachedResultsFor,

    // Methods - Samples
    fetchPendingSamples,
    fetchPendingSampleResults,
    updateSampleBarcode,
    deleteAllResults,
    publishAllResults,

    // Methods - Results
    fetchResultById,
    deleteResult,
    publishResult,
    updateResult,

    // Methods - Visits
    fetchPendingVisits,
    fetchPendingVisitResults,
    moveCachedResults,

    // Helpers
    isStat,
    clearAll,
    clearCache,
    abortAll,
  };
};
